import { css, html, LitElement } from 'lit';
import type {
  BracketInfo,
  BracketSize,
  CountdownTimer,
  GameMode,
  LobbyPlayer,
  RpsThrow,
  RpsThrowOrTimeout,
} from '@/shared/messages.ts';
import { BracketWsClient } from '../ws/client.ts';
import './biz-welcome.ts';
import { CODE_KEY } from './biz-welcome.ts';
import './biz-lobby.ts';
import './biz-popup.ts';
import './biz-response-entry.ts';
import './biz-voting.ts';
import './biz-tiebreaker.ts';

type Screen = 'welcome' | 'lobby' | 'response-entry' | 'voting' | 'tiebreaker' | 'complete';

type TiebreakerResult = {
  count: number;
  rpsA: RpsThrowOrTimeout;
  rpsB: RpsThrowOrTimeout;
  winnerId?: number;
};

export class BizApp extends LitElement {
  static properties = {
    _screen: { state: true },
    _players: { state: true },
    _hostUserId: { state: true },
    _size: { state: true },
    _mode: { state: true },
    _roomCode: { state: true },
    _startingTimer: { state: true },
    _errorMessage: { state: true },
    _prompt: { state: true },
    _slots: { state: true },
    _freeSlots: { state: true },
    _bracket: { state: true },
    _voteTopic: { state: true },
    _voteTimer: { state: true },
    _voteResponseAId: { state: true },
    _voteResponseBId: { state: true },
    _voteAVotes: { state: true },
    _voteBVotes: { state: true },
    _myVoteId: { state: true },
    _myLockedIn: { state: true },
    _voteEligible: { state: true },
    _voteResult: { state: true },
    _tbCount: { state: true },
    _tbUserIdA: { state: true },
    _tbUserIdB: { state: true },
    _tbTimer: { state: true },
    _tbMyThrow: { state: true },
    _tbResult: { state: true },
  };

  declare _screen: Screen;
  declare _players: LobbyPlayer[];
  declare _hostUserId: string;
  declare _size: BracketSize;
  declare _mode: GameMode;
  declare _roomCode: string;
  declare _startingTimer: CountdownTimer | null;
  declare _errorMessage: string;
  declare _prompt: string;
  declare _slots: number[];
  declare _freeSlots: number;
  declare _bracket: BracketInfo | null;
  declare _voteTopic: string;
  declare _voteTimer: CountdownTimer | null;
  declare _voteResponseAId: number;
  declare _voteResponseBId: number;
  declare _voteAVotes: number;
  declare _voteBVotes: number;
  declare _myVoteId: number;
  declare _myLockedIn: boolean;
  declare _voteEligible: boolean;
  declare _voteResult: { winnerId: number; aVotes: number; bVotes: number } | null;
  declare _tbCount: number;
  declare _tbUserIdA: string;
  declare _tbUserIdB: string;
  declare _tbTimer: CountdownTimer | null;
  declare _tbMyThrow: RpsThrow | '';
  declare _tbResult: TiebreakerResult | null;

  #ws = new BracketWsClient();

  constructor() {
    super();
    this._screen = 'welcome';
    this._players = [];
    this._hostUserId = '';
    this._size = 8;
    this._mode = 'basic';
    this._roomCode = '';
    this._startingTimer = null;
    this._errorMessage = '';
    this._prompt = '';
    this._slots = [];
    this._freeSlots = 0;
    this._bracket = null;
    this._voteTopic = '';
    this._voteTimer = null;
    this._voteResponseAId = 0;
    this._voteResponseBId = 0;
    this._voteAVotes = 0;
    this._voteBVotes = 0;
    this._myVoteId = 0;
    this._myLockedIn = false;
    this._voteEligible = true;
    this._voteResult = null;
    this._tbCount = 1;
    this._tbUserIdA = '';
    this._tbUserIdB = '';
    this._tbTimer = null;
    this._tbMyThrow = '';
    this._tbResult = null;
  }

  static styles = css`
    :host {
      display: block;
    }
  `;

  #onWake = () => {
    if (document.visibilityState !== 'visible') return;
    if (this.#ws.isOpen()) return;
    this.#reconnect();
  };

  async #reconnect() {
    await this.#ws.connect();
    await this.#ws.whenOpen();
    // Pull whatever state the server has for us; the handler is a no-op if
    // we aren't in a game (welcome screen), so it's always safe to send.
    this.#ws.send({ type: 'refresh' });
  }

  async connectedCallback() {
    super.connectedCallback();
    document.addEventListener('visibilitychange', this.#onWake);
    globalThis.addEventListener('online', this.#onWake);
    globalThis.addEventListener('focus', this.#onWake);

    await this.#ws.connect();
    await this.#ws.whenOpen();

    this.#ws.on('joined', (msg) => {
      if (!msg.success) {
        this._errorMessage = 'Could not join.';
        return;
      }
      this._roomCode = msg.roomCode;
      // Write room code to storage on lobby creation, in addition to on join.
      sessionStorage.setItem(CODE_KEY, msg.roomCode);
      this._screen = 'lobby';
    });

    this.#ws.on('lobby-updated', (msg) => {
      this._players = msg.players;
      this._hostUserId = msg.hostUserId;
      this._size = msg.size;
      this._mode = msg.mode;
      // Server doesn't echo the room code, but the client already knows it
      // from whichever path got the user here. If we just created a lobby
      // and don't yet have one, derive it from the join ack later — for now
      // it's set in #joinedFresh().
    });

    this.#ws.on('game-starting', (msg) => {
      this._startingTimer = msg.timer;
    });

    this.#ws.on('game-start-canceled', () => {
      this._startingTimer = null;
    });

    this.#ws.on('game-status', (msg) => {
      this._startingTimer = null;
      this._prompt = msg.bracket.prompt;
      this._bracket = msg.bracket;
      if (msg.bracket.round === 0) {
        this._screen = 'response-entry';
      } else if (msg.bracket.round === -1) {
        this._screen = 'complete';
      } else {
        // Render the round's topic during the inter-round delay, before the
        // delayed vote-start arrives. (vote-start will re-set this — same
        // value — when it lands.) Clear the prior matchup's IDs/result so
        // <biz-voting> hides the response cards until vote-start fires.
        const topics = msg.bracket.topics;
        this._voteTopic = topics[topics.length - 1] ?? '';
        this._voteResponseAId = 0;
        this._voteResponseBId = 0;
        this._voteResult = null;
        this._screen = 'voting';
      }
    });

    this.#ws.on('vote-start', (msg) => {
      this._voteResult = null;
      this._voteResponseAId = msg.responseIdA;
      this._voteResponseBId = msg.responseIdB;
      this._voteTimer = msg.timer;
      this._voteAVotes = 0;
      this._voteBVotes = 0;
      this._myVoteId = 0;
      this._myLockedIn = false;
      this._voteEligible = true;
      // Coming back from the tiebreaker screen (next matchup after RPS) or
      // resuming after refresh — make sure the screen state matches.
      this._screen = 'voting';
      this._tbResult = null;
      // Topic for this round is the last revealed one; spec says the
      // multi-blind topic is revealed at vote-start, so it lives in
      // bracket.topics already.
      const topics = this._bracket?.topics ?? [];
      this._voteTopic = topics[topics.length - 1] ?? '';
    });

    this.#ws.on('vote-status', (msg) => {
      this._voteTimer = msg.timer;
      this._voteAVotes = msg.aVotes;
      this._voteBVotes = msg.bVotes;
    });

    this.#ws.on('vote-result', (msg) => {
      this._voteResult = {
        winnerId: msg.winnerId,
        aVotes: msg.aVotes,
        bVotes: msg.bVotes,
      };
    });

    this.#ws.on('tiebreaker-needed', (msg) => {
      this._tbCount = msg.count;
      this._tbUserIdA = msg.userIdA;
      this._tbUserIdB = msg.userIdB;
      this._tbTimer = msg.timer;
      // Drop the prior throw when a new RPS round starts (count advanced) or
      // an unpause re-broadcast lands; the server only treats a fresh throw
      // as committed once the message reaches it.
      this._tbMyThrow = '';
      this._tbResult = null;
      this._screen = 'tiebreaker';
    });

    this.#ws.on('tiebreaker-result', (msg) => {
      this._tbResult = {
        count: msg.count,
        rpsA: msg.rpsA,
        rpsB: msg.rpsB,
        winnerId: msg.winnerId,
      };
      // If this is a draw, the next tiebreaker-needed will overwrite state
      // and clear _tbMyThrow. If it's decisive, the bracket advances via the
      // next vote-start or game-status (which set _screen accordingly).
    });

    this.#ws.on('enter-responses', (msg) => {
      // Server sends the player's full current unfilled slot list on every
      // update (initial assignment, own bonus claim, anyone else's bonus
      // claim), so we just replace.
      this._slots = msg.slots;
      this._freeSlots = msg.freeSlots;
    });

    this.#ws.on('error', (msg) => {
      if (msg.code === 'not-eligible') {
        this._voteEligible = false;
        return;
      }
      this._errorMessage = `${msg.code}: ${msg.message}`;
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('visibilitychange', this.#onWake);
    globalThis.removeEventListener('online', this.#onWake);
    globalThis.removeEventListener('focus', this.#onWake);
  }

  render() {
    return html`
      <link rel="stylesheet" href="/static/style.css" />
      ${this.#renderScreen()}
      <biz-popup
        .open="${!!this._errorMessage}"
        title="Hmm"
        @close="${() => (this._errorMessage = '')}"
      >
        ${this._errorMessage}
      </biz-popup>
    `;
  }

  #renderScreen() {
    if (this._screen === 'welcome') {
      return html`
        <biz-welcome
          @biz-join="${(e: CustomEvent) => this.#join(e.detail.name, e.detail.code)}"
          @biz-create="${(e: CustomEvent) => this.#join(e.detail.name, '')}"
        >
        </biz-welcome>
      `;
    }
    if (this._screen === 'voting') {
      const textFor = (id: number) => this._bracket?.responses.find((r) => r.id === id)?.text ?? '';
      const isHost = !!this.#ws.userId && this.#ws.userId === this._hostUserId;
      const roundLabel = this.#roundLabel();
      return html`
        <biz-voting
          .topic="${this._voteTopic}"
          .roundLabel="${roundLabel}"
          .responseAId="${this._voteResponseAId}"
          .responseAText="${textFor(this._voteResponseAId)}"
          .responseBId="${this._voteResponseBId}"
          .responseBText="${textFor(this._voteResponseBId)}"
          .aVotes="${this._voteAVotes}"
          .bVotes="${this._voteBVotes}"
          .timer="${this._voteTimer}"
          .eligible="${this._voteEligible}"
          .isHost="${isHost}"
          .myVoteId="${this._myVoteId}"
          .myLockedIn="${this._myLockedIn}"
          .result="${this._voteResult}"
          @biz-cast-vote="${(e: CustomEvent) =>
            this.#castVote(e.detail.responseId, e.detail.lockedIn)}"
          @biz-pause-game="${() => this.#ws.send({ type: 'pause-game' })}"
          @biz-unpause-game="${() => this.#ws.send({ type: 'unpause-game' })}"
        >
        </biz-voting>
      `;
    }
    if (this._screen === 'tiebreaker') {
      const textFor = (id: number) => this._bracket?.responses.find((r) => r.id === id)?.text ?? '';
      const nameFor = (uid: string) => this._players.find((p) => p.userId === uid)?.name ?? '';
      const isHost = !!this.#ws.userId && this.#ws.userId === this._hostUserId;
      const roundLabel = this.#roundLabel();
      return html`
        <biz-tiebreaker
          .topic="${this._voteTopic}"
          .roundLabel="${roundLabel}"
          .responseAId="${this._voteResponseAId}"
          .responseAText="${textFor(this._voteResponseAId)}"
          .responseBId="${this._voteResponseBId}"
          .responseBText="${textFor(this._voteResponseBId)}"
          .userIdA="${this._tbUserIdA}"
          .userIdB="${this._tbUserIdB}"
          .nameA="${nameFor(this._tbUserIdA)}"
          .nameB="${nameFor(this._tbUserIdB)}"
          .selfUserId="${this.#ws.userId ?? ''}"
          .count="${this._tbCount}"
          .timer="${this._tbTimer}"
          .isHost="${isHost}"
          .myThrow="${this._tbMyThrow}"
          .result="${this._tbResult}"
          @biz-break-tie="${(e: CustomEvent) => this.#breakTie(e.detail.throw)}"
          @biz-pause-game="${() => this.#ws.send({ type: 'pause-game' })}"
          @biz-unpause-game="${() => this.#ws.send({ type: 'unpause-game' })}"
        >
        </biz-tiebreaker>
      `;
    }
    if (this._screen === 'complete') {
      // Both finalists have votes recorded for every round; the winner is the
      // one whose final-round tally is higher.
      const responses = this._bracket?.responses ?? [];
      const finalRound = Math.log2(this._bracket?.size ?? 4);
      const finalists = responses.filter((r) => r.votes.length >= finalRound);
      const winner = finalists.reduce<typeof finalists[number] | undefined>(
        (best, r) =>
          !best || (r.votes[finalRound - 1] ?? 0) > (best.votes[finalRound - 1] ?? 0) ? r : best,
        undefined,
      );
      return html`
        <div style="text-align:center; margin-top:3rem;">
          <h1>Bracket complete</h1>
          ${winner
            ? html`
              <p>Winner: ${winner.text}</p>
            `
            : null}
        </div>
      `;
    }
    if (this._screen === 'response-entry') {
      return html`
        <biz-response-entry
          .prompt="${this._prompt}"
          .slots="${this._slots}"
          .freeSlots="${this._freeSlots}"
          @biz-submit-response="${(e: CustomEvent) =>
            this.#ws.send({
              type: 'submit-response',
              responseId: e.detail.responseId,
              response: e.detail.response,
            })}"
          @biz-claim-bonus="${() => this.#ws.send({ type: 'claim-bonus-response' })}"
        >
        </biz-response-entry>
      `;
    }
    return html`
      <biz-lobby
        .roomCode="${this._roomCode}"
        .players="${this._players}"
        .hostUserId="${this._hostUserId}"
        .selfUserId="${this.#ws.userId ?? ''}"
        .size="${this._size}"
        .mode="${this._mode}"
        .startingTimer="${this._startingTimer}"
        @biz-update-settings="${(e: CustomEvent) =>
          this.#ws.send({
            type: 'update-settings',
            size: e.detail.size,
            mode: e.detail.mode,
          })}"
        @biz-start-game="${() => this.#ws.send({ type: 'start-game' })}"
        @biz-cancel-start="${() => this.#ws.send({ type: 'cancel-start' })}"
        @biz-leave="${() => this.#leave()}"
      >
      </biz-lobby>
    `;
  }

  #join(name: string, code: string) {
    this.#ws.send({ type: 'join', name, code });
  }

  #leave() {
    this.#ws.send({ type: 'move-roles', role: 'exit' });
    sessionStorage.removeItem(CODE_KEY);
    this._roomCode = '';
    this._players = [];
    this._hostUserId = '';
    this._startingTimer = null;
    this._bracket = null;
    this._screen = 'welcome';
  }

  #roundLabel(): string {
    const bracket = this._bracket;
    if (!bracket || bracket.round <= 0) return '';
    // Responses still in play at the start of round `r` = size / 2^(r-1).
    const remaining = bracket.size / Math.pow(2, bracket.round - 1);
    if (remaining <= 2) return 'Final Matchup';
    if (remaining <= 4) return 'Semifinals';
    return `Round of ${remaining}`;
  }

  #breakTie(t: RpsThrow) {
    this._tbMyThrow = t;
    this.#ws.send({ type: 'break-tie', throw: t });
  }

  #castVote(responseId: number, lockedIn: boolean) {
    this._myVoteId = responseId;
    if (lockedIn) {
      this._myLockedIn = true;
    }
    this.#ws.send({ type: 'cast-vote', responseId, lockedIn });
  }
}

customElements.define('biz-app', BizApp);
